import { Skeleton } from "@mui/material";
import { styled } from "@mui/system";
import InfoIcon from "src/icons/portfolio/InfoIcon";
import TimePeriodDropdown from "../balance/TimePeriodDropdown";
import { ITimePeriodOption, SUMMARY_TOOLTIP, SUMMARY_TOOLTIPS } from "../defaults";
import QuestionMarkIcon from "src/icons/portfolio/QuestionMarkIcon";
import useIsUkUserCountry from "src/utils/hooks/useIsUkUserCountry";

export const SummaryHeader = styled("div")({
  display: "flex",
  width: "100%",
  justifyContent: "space-between",
  alignItems: "center",
});

export const SummarySubHeader = styled("div")({
  display: "flex",
  width: "138px",
  alignItems: "center",
  gap: "8px",
  flexShrink: 0,
  fontSize: "14px",
});

export const SummaryTileWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  height: "136px",
  width: "100%",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "12px",

  [theme.breakpoints.between(720, 1440)]: {
    height: "62px",
    alignItems: "flex-start",
    alignSelf: "stretch",
    flexDirection: "row",
  },
}));

export const SummaryTileRow = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  width: "100%",
});

export const SummaryTile = styled("div")(({ theme }) => ({
  width: "100%",
  height: "62px",
  padding: "8px",
  borderRadius: "10px",
  border: `1px solid ${theme.palette.secondary.contrastText}`,
  background: theme.palette.secondary.contrastText,
}));

export const SummaryTileMetric = styled("div")({
  fontWeight: 600,
  fontSize: "16px",
});

export const SummaryTileText = styled("div")({
  display: "flex",
  fontSize: "14px",
  alignItems: "center",
  gap: "8px",
});

interface SummaryDefaultProps {
  isLoading: boolean;
  timePeriodOptions: ITimePeriodOption[];
  howtos: string;
  handleHowTos: (newOpt: ITimePeriodOption) => void;
}
const SummaryDefault = ({
  isLoading,
  timePeriodOptions,
  howtos,
  handleHowTos,
}: SummaryDefaultProps) => {
  const isUkUser = useIsUkUserCountry();

  if (isLoading) return <Skeleton variant="rounded" width={"100%"} height={"208px"} />;
  return (
    <>
      {/* HEADER */}
      <SummaryHeader>
        <SummarySubHeader>
          Your summary <InfoIcon tooltip={SUMMARY_TOOLTIP} />
        </SummarySubHeader>
        <TimePeriodDropdown
          options={timePeriodOptions}
          timePeriod={howtos}
          onTimePeriodChange={handleHowTos}
          isHowTo={true}
        />
      </SummaryHeader>

      {/* TILES */}
      <SummaryTileWrapper>
        <SummaryTileRow>
          <SummaryTile>
            <SummaryTileMetric id="user-summary-targets" translate="no">0</SummaryTileMetric>
            <SummaryTileText>
              Targets <QuestionMarkIcon tooltip={SUMMARY_TOOLTIPS["Open targets"]} />
            </SummaryTileText>
          </SummaryTile>
          <SummaryTile>
            <SummaryTileMetric id="user-summary-trades" translate="no">0</SummaryTileMetric>
            <SummaryTileText>
              Trades <QuestionMarkIcon tooltip={SUMMARY_TOOLTIPS["Trades"]} />
            </SummaryTileText>
          </SummaryTile>
        </SummaryTileRow>
        <SummaryTileRow>
          <SummaryTile>
            <SummaryTileMetric id="user-summary-portfolio" translate="no">0</SummaryTileMetric>
            <SummaryTileText>
              Portfolio <QuestionMarkIcon tooltip={SUMMARY_TOOLTIPS["Portfolio"]} />
            </SummaryTileText>
          </SummaryTile>
          {!isUkUser && (
            <SummaryTile>
              <SummaryTileMetric id="user-summary-referrals" translate="no">0</SummaryTileMetric>
              <SummaryTileText>
                Referrals <QuestionMarkIcon tooltip={SUMMARY_TOOLTIPS["Referrals"]} />
              </SummaryTileText>
            </SummaryTile>
          )}
        </SummaryTileRow>
      </SummaryTileWrapper>
    </>
  );
};

export default SummaryDefault;
